<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2">Cargando calendario de evaluaciones...</p>
    </div>

    <!-- Selector de Programación -->
    <b-card v-if="!reporte" class="mb-3">
      <b-card-title>
        <feather-icon icon="CalendarIcon" size="20" class="mr-1" />
        Reporte de Calendario de Evaluaciones
      </b-card-title>

      <b-row>
        <b-col md="6">
          <b-form-group label="Programación Académica*">
            <v-select
              v-model="selectedProgramacion"
              :options="programaciones"
              label="descripcion"
              placeholder="Seleccione una programación"
            >
              <template #option="{ periodoCodigo, carreraNombre, tipoPlanNombre }">
                {{ periodoCodigo }} - {{ carreraNombre }} - {{ tipoPlanNombre }}
              </template>
              <template #selected-option="{ periodoCodigo, carreraNombre, tipoPlanNombre }">
                {{ periodoCodigo }} - {{ carreraNombre }} - {{ tipoPlanNombre }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6" class="d-flex align-items-end">
          <b-button
            variant="primary"
            @click="loadReporte"
            :disabled="!selectedProgramacion || loading"
          >
            <feather-icon icon="SearchIcon" class="mr-1" />
            Generar Reporte
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Contenido Principal del Reporte -->
    <b-card v-if="reporte" id="reporte-calendario">
      <!-- Header con Logo y Universidad -->
      <div class="header mb-4">
        <div class="logo-section">
          <div class="logo-placeholder">
            <feather-icon icon="ImageIcon" size="24" />
            <span>LOGOTIPO UNIVERSIDAD</span>
          </div>
        </div>
        <div class="university-info text-center">
          <h4 class="university-name mb-1">UNIVERSIDAD MARIANO GÁLVEZ DE GUATEMALA</h4>
          <p class="university-center mb-0">CENTRO UNIVERSITARIO: CAMPUS VILLA NUEVA</p>
        </div>
      </div>

      <!-- Título del Reporte -->
      <div class="report-title mb-4">
        <h5>PROGRAMACIÓN DE CALENDARIO DE EVALUACIONES {{ periodoNombre }}</h5>
      </div>

      <!-- Información de Carrera y Sección -->
      <div class="career-section mb-4">
        <b-row>
          <b-col md="8">
            <strong>Carrera:</strong> <strong class="text-primary">{{ reporte.carreraNombre }}</strong>
            <br>
            <strong>Plan:</strong> {{ reporte.tipoPlanNombre }}
          </b-col>
          <b-col md="4" class="text-right">
            <strong>SECCIÓN:</strong> {{ reporte.seccion }} | 
            <strong>SALÓN:</strong> {{ reporte.salonNumero || 'N/A' }} | 
            <strong>CUPO:</strong> {{ reporte.cupo || 'N/A' }}
          </b-col>
        </b-row>
      </div>

      <!-- Botones de Acción -->
      <div class="action-buttons mb-4 no-print">
        <b-button
          variant="outline-danger"
          @click="exportToPDF"
          class="mr-2"
        >
          <feather-icon icon="DownloadIcon" class="mr-1" />
          Exportar PDF
        </b-button>

        <b-button
          variant="outline-secondary"
          @click="volverALista"
        >
          <feather-icon icon="ArrowLeftIcon" class="mr-1" />
          Volver
        </b-button>
      </div>

      <!-- Tabla de Evaluaciones -->
      <div class="evaluation-table">
        <b-table
          :items="reporte.cursos"
          :fields="tableFields"
          responsive
          striped
          hover
          class="mb-0 evaluation-calendar-table"
        >
          <template #thead-top>
            <b-tr>
              <b-th colspan="4"></b-th>
              <b-th colspan="6" class="text-center">Tipo de Evaluación / Fecha</b-th>
            </b-tr>
          </template>

          <template #cell(codigo)="{ item }">
            <div class="text-center">
              <strong>{{ item.codigoCiclo || 'II' }}</strong>
              <br>
              <small class="text-muted">{{ item.codigoCurso }}</small>
            </div>
          </template>

          <template #cell(curso)="{ item }">
            <strong>{{ item.nombreCurso }}</strong>
          </template>

          <template #cell(dia)="{ item }">
            <b-badge variant="info">{{ item.diaSemana }}</b-badge>
          </template>

          <template #cell(horario)="{ item }">
            <small>{{ item.horarioClases }}</small>
          </template>

          <template #cell(parcial1)="{ item }">
            <div class="evaluation-date" v-if="getEvaluacion(item, '1er Parcial')">
              {{ getEvaluacion(item, '1er Parcial').fechaFormateada }}
            </div>
            <div v-else class="text-muted">-</div>
          </template>

          <template #cell(extraordinario1)="{ item }">
            <div class="evaluation-date" v-if="getEvaluacion(item, 'Extraordinario 1')">
              {{ getEvaluacion(item, 'Extraordinario 1').fechaFormateada }}
            </div>
            <div v-else class="text-muted">-</div>
          </template>

          <template #cell(parcial2)="{ item }">
            <div class="evaluation-date" v-if="getEvaluacion(item, '2do Parcial')">
              {{ getEvaluacion(item, '2do Parcial').fechaFormateada }}
            </div>
            <div v-else class="text-muted">-</div>
          </template>

          <template #cell(extraordinario2)="{ item }">
            <div class="evaluation-date" v-if="getEvaluacion(item, 'Extraordinario 2')">
              {{ getEvaluacion(item, 'Extraordinario 2').fechaFormateada }}
            </div>
            <div v-else class="text-muted">-</div>
          </template>

          <template #cell(final)="{ item }">
            <div class="evaluation-date" v-if="getEvaluacion(item, 'Evaluación Final')">
              {{ getEvaluacion(item, 'Evaluación Final').fechaFormateada }}
            </div>
            <div v-else class="text-muted">-</div>
          </template>

          <template #cell(recuperacion)="{ item }">
            <div class="evaluation-date" v-if="getEvaluacion(item, 'Recuperación')">
              {{ getEvaluacion(item, 'Recuperación').fechaFormateada }}
            </div>
            <div v-else class="text-muted">-</div>
          </template>
        </b-table>
      </div>

      <!-- Firmas -->
      <div class="signatures-section mt-5 pt-4 border-top">
        <b-row>
          <b-col md="6">
            <div class="signature text-center">
              <div class="signature-line mb-2"></div>
              <p class="mb-1"><strong>Vb. Bo. Lic. Juan Alberto Monzón Esquivel (2256)</strong></p>
              <p class="mb-1">Firma del Coordinador de la Facultad</p>
              <p class="text-muted">Centro Universitario de: Campus Villa Nueva</p>
            </div>
          </b-col>
          <b-col md="6">
            <div class="signature text-center">
              <div class="signature-line mb-2"></div>
              <p class="mb-1"><strong>Vb. Bo. Dr. Manfredo Osorio Bolaños</strong></p>
              <p class="mb-1">Dirección General de Centros Universitarios</p>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Footer -->
      <div class="footer text-center mt-4">
        <small class="text-muted">Formato por Huehutenango 2024</small>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BRow, BCol, BButton, BSpinner, BTable, BBadge,
  BFormGroup, BTr, BTh
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import programacionesService from '@/services/programacionesService'
import calendarioEvaluacionService from '@/services/calendarioEvaluacionService'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'

export default {
  name: 'CalendarioEvaluaciones',
  components: {
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BFormGroup,
    BTr,
    BTh,
    vSelect,
  },
  data() {
    return {
      loading: false,
      selectedProgramacion: null,
      programaciones: [],
      reporte: null,
      periodoNombre: '',
      
      tableFields: [
        { key: 'codigo', label: 'Código de Carrera y Código de Curso', sortable: false },
        { key: 'curso', label: 'NOMBRE DEL CURSO', sortable: false },
        { key: 'dia', label: 'Día', sortable: false },
        { key: 'horario', label: 'Horario de Clases', sortable: false },
        { key: 'parcial1', label: '1er Parcial' },
        { key: 'extraordinario1', label: 'Extraordinario' },
        { key: 'parcial2', label: '2do Parcial' },
        { key: 'extraordinario2', label: 'Extraordinario' },
        { key: 'final', label: 'Evaluación Final' },
        { key: 'recuperacion', label: 'Recuperación' },
      ],
    }
  },
  async mounted() {
    await this.loadProgramaciones()
  },
  methods: {
    async loadProgramaciones() {
      this.loading = true
      try {
        const data = await programacionesService.getAll()
        this.programaciones = data.map(p => ({
          ...p,
          descripcion: `${p.periodoCodigo} - ${p.carreraNombre} - ${p.tipoPlanNombre || 'Sin plan'}`
        }))
      } catch (error) {
        console.error('Error loading programaciones:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Error al cargar las programaciones académicas',
          },
        })
      } finally {
        this.loading = false
      }
    },

    async loadReporte() {
      if (!this.selectedProgramacion) return

      this.loading = true
      try {
        this.reporte = await calendarioEvaluacionService.getReporte(
          this.selectedProgramacion.programacionAcademicaId
        )
        this.periodoNombre = `SEGUNDO SEMESTRE ${new Date().getFullYear()}`
        
        console.log('Reporte cargado:', this.reporte)
      } catch (error) {
        console.error('Error loading reporte:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Error al cargar el reporte de evaluaciones',
          },
        })
      } finally {
        this.loading = false
      }
    },

    getEvaluacion(curso, tipoEvaluacion) {
      if (!curso.evaluaciones) return null
      return curso.evaluaciones.find(e => e.tipoEvaluacion === tipoEvaluacion)
    },

    volverALista() {
      this.reporte = null
      this.selectedProgramacion = null
    },

    exportToPDF() {
      try {
        const doc = new jsPDF('l', 'mm', 'letter') // Landscape, millimeters, letter size

        // Header
        doc.setFontSize(16)
        doc.setFont('helvetica', 'bold')
        doc.text('UNIVERSIDAD MARIANO GÁLVEZ DE GUATEMALA', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' })
        
        doc.setFontSize(12)
        doc.text('CENTRO UNIVERSITARIO: CAMPUS VILLA NUEVA', doc.internal.pageSize.getWidth() / 2, 22, { align: 'center' })
        
        doc.setFontSize(14)
        doc.text(`PROGRAMACIÓN DE CALENDARIO DE EVALUACIONES ${this.periodoNombre}`, doc.internal.pageSize.getWidth() / 2, 30, { align: 'center' })

        // Career info
        doc.setFontSize(10)
        doc.setFont('helvetica', 'normal')
        doc.text(`Carrera: ${this.reporte.carreraNombre}`, 14, 40)
        doc.text(`Plan: ${this.reporte.tipoPlanNombre}`, 14, 45)
        doc.text(`Sección: ${this.reporte.seccion}`, 180, 40)
        doc.text(`Salón: ${this.reporte.salonNumero || 'N/A'}`, 180, 45)

        // Table data
        const tableData = this.reporte.cursos.map(curso => {
          return [
            `${curso.codigoCiclo || 'II'}\n${curso.codigoCurso}`,
            curso.nombreCurso,
            curso.diaSemana,
            curso.horarioClases,
            this.getEvaluacion(curso, '1er Parcial')?.fechaFormateada || '-',
            this.getEvaluacion(curso, 'Extraordinario 1')?.fechaFormateada || '-',
            this.getEvaluacion(curso, '2do Parcial')?.fechaFormateada || '-',
            this.getEvaluacion(curso, 'Extraordinario 2')?.fechaFormateada || '-',
            this.getEvaluacion(curso, 'Evaluación Final')?.fechaFormateada || '-',
            this.getEvaluacion(curso, 'Recuperación')?.fechaFormateada || '-',
          ]
        })

        doc.autoTable({
          startY: 52,
          head: [[
            'Código',
            'Nombre del Curso',
            'Día',
            'Horario',
            '1er Parcial',
            'Extraordinario',
            '2do Parcial',
            'Extraordinario',
            'Evaluación Final',
            'Recuperación'
          ]],
          body: tableData,
          styles: {
            fontSize: 8,
            cellPadding: 2,
          },
          headStyles: {
            fillColor: [44, 62, 80],
            textColor: 255,
            fontStyle: 'bold',
          },
          columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 40 },
            2: { cellWidth: 18 },
            3: { cellWidth: 28 },
            4: { cellWidth: 22 },
            5: { cellWidth: 22 },
            6: { cellWidth: 22 },
            7: { cellWidth: 22 },
            8: { cellWidth: 22 },
            9: { cellWidth: 22 },
          },
        })

        // Signatures
        const finalY = doc.lastAutoTable.finalY + 15
        doc.text('____________________________________', 40, finalY)
        doc.text('Vb. Bo. Coordinador', 50, finalY + 5)
        
        doc.text('____________________________________', 140, finalY)
        doc.text('Vb. Bo. Director', 155, finalY + 5)

        // Save
        doc.save(`calendario-evaluaciones-${this.reporte.carreraNombre}.pdf`)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'PDF Generado',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'El calendario se ha exportado correctamente en formato PDF',
          },
        })
      } catch (error) {
        console.error('Error exporting PDF:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Error al generar el PDF',
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.logo-placeholder {
  width: 150px;
  height: 120px;
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 12px;
  text-align: center;
}

.university-name {
  font-size: 1.25rem;
  font-weight: bold;
  color: #2c3e50;
}

.university-center {
  font-size: 1rem;
  color: #6c757d;
}

.report-title {
  text-align: center;
  border-bottom: 2px solid #3498db;
  padding-bottom: 0.5rem;
}

.report-title h5 {
  color: #2c3e50;
  margin: 0;
}

.career-section {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #3498db;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.evaluation-calendar-table >>> th {
  background-color: #2c3e50;
  color: white;
  text-align: center;
  vertical-align: middle;
  font-size: 0.875rem;
}

.evaluation-calendar-table >>> td {
  vertical-align: middle;
  font-size: 0.875rem;
}

.evaluation-date {
  text-align: center;
  padding: 0.5rem;
}

.signature-line {
  border-top: 1px solid #2c3e50;
  width: 200px;
  margin: 0 auto;
}

.signature p {
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

.footer {
  font-size: 0.75rem;
}

/* Print Styles */
@media print {
  .no-print {
    display: none !important;
  }
  
  .header {
    flex-direction: column;
    text-align: center;
  }
  
  .logo-placeholder {
    margin-bottom: 1rem;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    text-align: center;
  }
  
  .logo-placeholder {
    margin-bottom: 1rem;
  }
  
  .action-buttons {
    justify-content: center;
  }
  
  .evaluation-calendar-table {
    font-size: 0.75rem;
  }
}
</style>
